import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Logo from "../../assets/logo-alfa-tabelas.png";
import { baseUrl } from "../../services/api/index";
import "./reset-password.css";

const genericError = "Não foi possível concluir a solicitação. Tente novamente.";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState(token ? "loading" : "forgot");
  const [retry, setRetry] = useState(0);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const pending = useRef(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (!token) {
      setStatus("forgot");
      setMessage("");
      return;
    }
    const controller = new AbortController();
    setStatus("loading");
    setMessage("");
    async function validateToken() {
      try {
        const response = await fetch(
          `${baseUrl}/auth/is-token-valid?token=${encodeURIComponent(token)}`,
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        if (response.status === 400 || response.status === 404) {
          setStatus("invalid");
          return;
        }
        if (!response.ok) throw new Error("Validation failed");
        const data = await response.json();
        if (!controller.signal.aborted) setStatus(data.valid === true ? "valid" : "invalid");
      } catch (error) {
        if (!controller.signal.aborted && error.name !== "AbortError") {
          setStatus("error");
          setMessage(genericError);
        }
      }
    }
    validateToken();
    return () => controller.abort();
  }, [token, retry]);

  useEffect(() => {
    if (status === "invalid" || status === "success") titleRef.current?.focus();
  }, [status]);

  async function handleRequestEmail(event) {
    event.preventDefault();
    if (pending.current || (status !== "forgot" && status !== "forgot-success")) return;
    if (!email.trim()) return setMessage("Informe seu e-mail.");
    pending.current = true;
    setStatus("requesting");
    setMessage("");
    try {
      const response = await fetch(`${baseUrl}/auth/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) throw new Error("Request failed");
      setStatus("forgot-success");
      setMessage("Se o e-mail estiver cadastrado, você receberá as instruções de redefinição.");
    } catch {
      setStatus("forgot");
      setMessage(genericError);
    } finally {
      pending.current = false;
    }
  }

  async function handleSubmitPassword(event) {
    event.preventDefault();
    if (pending.current || status !== "valid") return;
    if (!password) return setMessage("Informe a nova senha.");
    if (!confirmation) return setMessage("Confirme a nova senha.");
    if (password !== confirmation) return setMessage("As senhas não coincidem.");
    if (password.length < 8 || password.length > 16) {
      return setMessage("Senha deve ter entre 8 e 16 caracteres.");
    }
    pending.current = true;
    setStatus("submitting");
    setMessage("");
    try {
      const response = await fetch(`${baseUrl}/auth/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      if (response.ok) {
        setStatus("success");
      } else if (response.status === 400 || response.status === 404) {
        setStatus("invalid");
      } else {
        setStatus("valid");
        setMessage(genericError);
      }
    } catch {
      setStatus("valid");
      setMessage(genericError);
    } finally {
      pending.current = false;
    }
  }

  const isForgot = ["forgot", "requesting", "forgot-success"].includes(status);
  const isReset = status === "valid" || status === "submitting";
  return (
    <div className="reset-password-page">
      <main className="reset-password-card">
        <div className="reset-password-logo"><img src={Logo} alt="Alfa Tabelas" /></div>
        {isForgot && <section className="reset-password-content">
          <h1>Recuperar senha</h1>
          <p className="reset-password-description">
            Informe seu e-mail para receber um link de recuperação.
          </p>
          <form onSubmit={handleRequestEmail} noValidate>
            <div className="reset-password-field">
              <input id="reset-email" type="email" autoComplete="email" value={email}
                onChange={(event) => setEmail(event.target.value)} disabled={status === "requesting"} required />
              <label htmlFor="reset-email">E-mail</label>
            </div>
            <button className="reset-password-primary" type="submit" disabled={status === "requesting"}>
              {status === "requesting" ? <><span className="reset-password-spinner" aria-hidden="true" /> Enviando...</> : "Enviar link de recuperação"}
            </button>
          </form>
        </section>}
        {status === "loading" && <div className="reset-password-status" role="status" aria-live="polite">
          <h1>Validando seu link de recuperação...</h1>
          <span className="reset-password-spinner" aria-hidden="true" />
        </div>}
        {isReset && <section className="reset-password-content">
          <h1>Redefinir senha</h1>
          <p className="reset-password-description">
            Escolha uma nova senha para sua conta.
          </p>
          <form onSubmit={handleSubmitPassword} noValidate>
            <div className="reset-password-field">
              <input id="new-password" type="password" autoComplete="new-password" value={password}
                onChange={(event) => setPassword(event.target.value)} disabled={status === "submitting"} required />
              <label htmlFor="new-password">Nova senha</label>
            </div>
            <div className="reset-password-field">
              <input id="confirm-password" type="password" autoComplete="new-password" value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)} disabled={status === "submitting"} required />
              <label htmlFor="confirm-password">Confirmar nova senha</label>
            </div>
            <button className="reset-password-primary" type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? <><span className="reset-password-spinner" aria-hidden="true" /> Redefinindo...</> : "Redefinir senha"}
            </button>
          </form>
        </section>}
        {status === "invalid" && <div className="reset-password-status">
          <h1 ref={titleRef} tabIndex={-1}>Link de recuperação inválido</h1>
          <p>Este link expirou ou já foi utilizado. Solicite um novo link para redefinir sua senha.</p>
          <button className="reset-password-primary" type="button"
            onClick={() => navigate("/reset-password", { replace: true })}>Solicitar novo link</button>
        </div>}
        {status === "success" && <div className="reset-password-status" aria-live="polite">
          <h1 ref={titleRef} tabIndex={-1}>Senha alterada com sucesso</h1>
          <p>Sua senha foi atualizada. Use a nova senha para acessar sua conta.</p>
          <button className="reset-password-primary" type="button" onClick={() => navigate("/")}>Ir para o login</button>
        </div>}
        {status === "error" && <div className="reset-password-status">
          <h1>Não foi possível validar o link</h1>
          <button className="reset-password-primary" type="button"
            onClick={() => setRetry((value) => value + 1)}>Tentar novamente</button>
        </div>}
        {message && <p className={`reset-password-message ${status === "forgot-success" ? "reset-password-message-success" : ""}`}
          role={status === "forgot-success" ? "status" : "alert"}
          aria-live={status === "forgot-success" ? "polite" : "assertive"}>{message}</p>}
      </main>
    </div>
  );
}
